import React, { useEffect, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { AOWithProjectDTO } from "../types/Project";
import { useNavigate } from "react-router-dom"; 
import { projectService } from "../services/ProjetService";
import { useAuth } from "../context/AuthContext";
import AuthentifiedPage from "./AuthentifiedPage";
import { Lot } from "../types/Project";
import "../styles/GestionProjet.css";

const GestionProjet: React.FC = () => {
  const { api } = useAuth();
  const [aoWithProjects, setAoWithProjects] = useState<AOWithProjectDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AOWithProjectDTO | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();
  const [lots, setLots] = useState<Lot[]>([]);
  const [showCreateLotPopup, setShowCreateLotPopup] = useState(false);
  const [newLotLabel, setNewLotLabel] = useState("");
  const [newLotPercentage, setNewLotPercentage] = useState<number>(0);
  const [editingLotId, setEditingLotId] = useState<number | null>(null);
  const [editedPercentage, setEditedPercentage] = useState<number>(0);
  const [expandedTitles, setExpandedTitles] = React.useState<Set<number>>(new Set());

  useEffect(() => {
    const loadAOWithProjects = async () => {
      try {
        setLoading(true);
        const data = await projectService.getAOWithProjects(api);
        setAoWithProjects(data);
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement des AO avec projets :", error);
        setLoading(false);
      }
    };
    loadAOWithProjects();
  }, [api]);

  const handleAction = async (item: AOWithProjectDTO) => {

    if (!item.project?.idProject) {
      console.warn("Aucun ID projet trouvé");
      return;
    }

    try {
      const project = await projectService.getProjectById(api, item.project.idProject);

      setLots(project.lots ?? []);

      setSelectedItem(item);
      setShowActionPopup(true);

    } catch (error) {
      console.error("Erreur lors du chargement du projet :", error);
    }
  };

  const handleCreateLot = () => {
    if (!selectedItem?.project?.idProject) {
      alert("Aucun projet n'est sélectionné");
      return;
    }

    setNewLotLabel("");
    setNewLotPercentage(0);

    setShowCreateLotPopup(true);
  };

  const handleSubmitCreateLot = async () => {
    if (!selectedItem?.project?.idProject) return;

    const idProject = selectedItem.project.idProject;
    const lot: Lot = {
      label: newLotLabel,
      percentage: newLotPercentage,
      idProject: idProject
    };

    try {
      await projectService.createLot(api, idProject, lot);

      const updatedProject = await projectService.getProjectById(api, idProject);
      setLots(updatedProject.lots ?? []);

      setShowCreateLotPopup(false);
      alert("Lot créé avec succès !");
    } catch (error) {
      console.error("Erreur création lot :", error);
      alert("Impossible de créer le lot");
    }
  };

  const handleSavePercentage = async (lot: Lot) => {
    if (!lot.idLot) return;

    try {
      const message = await projectService.updateLotPercentage(api, lot.idLot, editedPercentage);

      if (selectedItem?.project?.idProject) {
        const updatedProject = await projectService.getProjectById(api, selectedItem.project.idProject);
        setLots(updatedProject.lots ?? []);
      }

      setEditingLotId(null);
      alert("Pourcentage mis à jour !");
    } catch (error) {
      console.error("Erreur mise à jour pourcentage :", error);
      alert("Impossible de mettre à jour le pourcentage");
    }
  };

  const filtered = useMemo(() => {
    let list = aoWithProjects.slice();
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (item) =>
          item.ao.idAo?.toString().includes(q) ||
          item.ao.titre.toLowerCase().includes(q) ||
          item.project?.idProject?.toString().includes(q)
      );
    }
    return list;
  }, [aoWithProjects, query]);    

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        <h4 className="mb-3">Liste des projets</h4>

        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher par ID projet / titre "
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-hover align-middle">
            <thead className="table-success">
              <tr>
                <th>ID Projet</th>
                <th>Titre</th>
                <th>Date début</th>
                <th>Date fin</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {pageData.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="text-center">Aucun projet trouvé</td>
                </tr>
              )}

              {pageData.map((item) => {
                const idAo = item.ao.idAo;
                if (idAo === undefined) return null;

                const isExpanded = expandedTitles.has(idAo);

                const toggleTitle = () => {
                  const newSet = new Set(expandedTitles);
                  if (newSet.has(idAo)) {
                    newSet.delete(idAo);
                  } else {
                    newSet.add(idAo);
                  }
                  setExpandedTitles(newSet);
                };

                return (
                  <tr key={idAo}>
                    <td>{item.project?.idProject ?? "—"}</td>
                    <td
                      onClick={toggleTitle}
                      style={{ cursor: "pointer" }}
                      title={item.ao.titre}
                    >
                      {isExpanded
                        ? item.ao.titre
                        : item.ao.titre.length > 50
                        ? item.ao.titre.slice(0, 50) + "..."
                        : item.ao.titre}
                    </td>
                    <td>{item.project?.startDate ? new Date(item.project.startDate).toLocaleString("fr-FR") : "—"}</td>
                    <td>{item.project?.endDate ? new Date(item.project.endDate).toLocaleString("fr-FR") : "—"}</td>
                    <td className="text-center">
                      <button 
                        className="btn btn-light btn-sm"
                        onClick={() => handleAction(item)}
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showActionPopup && (
          <div className="action-overlay">
            <div className="action-popup">
              <h3 className="action-title">Liste des lots</h3>

              <div className="action-header">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleCreateLot()}
                >
                  <i className="bi bi-plus-circle"></i> Créer un lot
                </button>
              </div>

              <table className="table table-bordered action-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Label</th>
                    <th>Pourcentage</th>
                    <th>ID Project</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {lots.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center">Aucun lot</td>
                    </tr>
                  )}

                  {lots.map((lot) => (
                    <tr key={lot.idLot}>
                      <td>{lot.idLot}</td>
                      <td>{lot.label}</td>

                      <td>
                        {editingLotId === lot.idLot ? (
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={editedPercentage}
                            onChange={(e) => setEditedPercentage(Number(e.target.value))}
                          />
                        ) : (
                          `${lot.percentage}%`
                        )}
                      </td>

                      <td>{lot.idProject}</td>

                      <td>
                        {editingLotId === lot.idLot ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-1"
                              onClick={() => handleSavePercentage(lot)}
                            >
                              Sauvegarder
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingLotId(null)}
                            >
                              Annuler
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setEditingLotId(lot.idLot!);
                              setEditedPercentage(lot.percentage);
                            }}
                          >
                            Modifier
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>

              <div className="action-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowActionPopup(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreateLotPopup && (
          <div className="action-overlay">
            <div className="action-popup">
              <h4 className="action-title">Créer un lot</h4>

              <div className="mb-2">
                <label>Label</label>
                <input
                  type="text"
                  className="form-control"
                  value={newLotLabel}
                  onChange={(e) => setNewLotLabel(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <label>Pourcentage</label>
                <input
                  type="number"
                  className="form-control"
                  value={newLotPercentage}
                  onChange={(e) => setNewLotPercentage(Number(e.target.value))}
                />
              </div>

              <div className="action-footer">
                <button
                  className="btn btn-secondary me-2"
                  onClick={() => setShowCreateLotPopup(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleSubmitCreateLot}
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">{pageData.length} projets affichés sur {total}</small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>{num}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </main>
    </AuthentifiedPage>
  );
};

export default GestionProjet;